import React, { ReactNode, useMemo } from "react";
import { api } from "api/api";
import { useTypedSelector } from "store";
import { BuildMacroResponseResult } from "store/reducers/products/macros/macros.utils";
import { Alert, DialogContentText } from "@mui/material";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { ActionButtonConfirmStatusWithPolling } from "components";
import { StandardSubproperties } from "store/reducers/products/subproperties/pl-subproperties.types";
import { UpdateUnitSchemaProcess } from "types/process";
import { UseProcessPollingOptions } from "hooks/useProcessPolling";
import {
  MacroBulletItem,
  MacroBulletList,
  MacroContentStack,
} from "./MacroButton.component";

export default function UpdateUnitSchemaButton() {
  const productLineId = useTypedSelector(
    (state) => state.products.selectedProductLine?.id
  );

  /** BEGIN ActionButtonConfirmStatusWithPolling Implementation */

  /**
   * startRequest:
   *    - Called once when the user confirms the action (clicks OK/Export).
   *    - Should start a backend process and return the initial process object.
   *    - Throw to surface an immediate validation/setup error in the dialog.
   */
  const startRequest = async (): Promise<UpdateUnitSchemaProcess> => {
    if (!productLineId) {
      throw new Error("No product line selected.");
    }

    //Start the Update Unit Schema Macro
    const response = await api.updateUnitSchema(productLineId);
    return response.data;
  };

  /**
   * pollRequest:
   *    - Polls the server for updated process state by process id.
   *    - Called repeatedly (pollIntervalMs) until the process reaches a terminal state.
   *    - Must return the latest process object by id.
   */
  const pollRequest = (id: string): Promise<UpdateUnitSchemaProcess> => {
    const response = api
      .getProcess(id)
      .then((r) => r.data as UpdateUnitSchemaProcess);
    return response;
  };

  /**
   * Called once after the process reaches a terminal state.
   * On success, downloads the generated export file.
   * onTerminal:
   *    - Called once after polling ends (terminal state reached).
   *    - Typical use: download output files, show toasts, do follow-up API calls.
   *    - Throw to mark the dialog as ERROR (ModalDialog will surface the error).
   */
  const onTerminal = async (): //process: UpdateUnitSchemaProcess
  Promise<void> => {
    //do nothing when process finishes
    return;
  };

  /**
   * Options object consumed by useProcessPolling (via ActionButtonConfirmStatusWithPolling).
   * Keep this object as “wiring only” to stay readable.
   */
  const processPollingOptions: UseProcessPollingOptions<
    UpdateUnitSchemaProcess,
    UpdateUnitSchemaProcess
  > = {
    startRequest,
    pollRequest,
    pollIntervalMs: 2000,
    onTerminal,
  };

  // Build the content section for the dialog
  const contentSection = useMemo<ReactNode>(() => {
    return (
      <MacroContentStack>
        <DialogContentText>
          This macro will convert a productline that uses the old integer based
          UNIT conversion schema to the new string based schema. If you do not
          know what this means, <strong>DO NOT PROCEED FURTHER!!!!</strong>
        </DialogContentText>
        <DialogContentText>
          Clicking on the <strong>OK</strong> button below will do the following
          on <em>ALL</em> RANGE and OPEN properties in the productline that have
          an INTEGER <strong>.UNIT</strong> subproperty:
        </DialogContentText>

        <MacroBulletList>
          <MacroBulletItem>
            Create <strong>{StandardSubproperties.DISPLAYUNIT}</strong>{" "}
            subproperty with both IP and SI units from all
            <strong> old .UNIT</strong> unit indexes.
          </MacroBulletItem>
          <MacroBulletItem>
            Create <strong>{StandardSubproperties.DISPLAYUNIT_DEFAULT}</strong>{" "}
            subproperty with the IP unit for the existing{" "}
            <strong>.UNIT.DEFAULT</strong> unit index. If there are more than
            one old unit index values, the property will not be able to be
            automatically converted.
          </MacroBulletItem>
          <MacroBulletItem>
            Create a new <strong>{StandardSubproperties.UNIT}</strong>{" "}
            subproperty with the IP unit for the{" "}
            <strong>old .UNIT.DEFAULT</strong> entry if it exists. Otherwise it
            uses the IP unit for the first <strong> old .UNIT</strong> unit
            index.
          </MacroBulletItem>
          <MacroBulletItem>
            Any <strong> old .UNIT</strong> index of 0 will be interpreted as
            the property having no units. In this case <em>none</em> of the new
            subproperties will be created.
          </MacroBulletItem>
          <MacroBulletItem>
            Delete the old <strong>.UNIT.DEFAULT</strong> subproperty.
          </MacroBulletItem>
          <MacroBulletItem>
            Delete the old <strong>.UNIT</strong> subproperty. In any tables
            that use that property, swap in the new property and the new IP
            values for existing unit indexes. Note: this may leave the table
            with invalid values which will need to manually addressed. Typically
            .UNIT is no longer a dependency in any table because there is only
            one value.
          </MacroBulletItem>
        </MacroBulletList>

        <Alert variant="outlined" severity="info" sx={{ mt: 1 }}>
          You will need to run a full productline validation after running this
          macro and manually fix any issues that are found.
        </Alert>

        <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
          <strong>Backup your productline before running this!!!!</strong> This
          will make many changes to the productline which cannot be undone.
        </Alert>
      </MacroContentStack>
    );
  }, []);

  //Callback for the process polling hook to generate results on process completion
  const buildResultSection = (process: UpdateUnitSchemaProcess): ReactNode => {
    return process.macroResponse
      ? BuildMacroResponseResult(process.macroResponse, false)
      : null;
  };

  return (
    <ActionButtonConfirmStatusWithPolling<
      UpdateUnitSchemaProcess,
      UpdateUnitSchemaProcess
    >
      //Button Props
      variant="outlined"
      color="secondary"
      disabled={!productLineId}
      //buttonFullWidth={true}
      //Icon Props
      icon={BuildCircleIcon}
      iconOnly={false}
      //Tooltip Props
      tooltipText="Updates the unit conversion schema from integer indexes to strings for the productline"
      tooltipArrow={true}
      tooltipPlacement="right"
      //Dialog Props
      maxWidth="md"
      fullWidth
      dialogTitle="Update Unit Schema"
      disableEscapeKeyDown={true}
      disableCloseOnBackdropClick={true}
      //Content Section Props
      contentSection={contentSection}
      hideContentOnProcessComplete={true}
      //Action Button Props
      defaultButton="CANCEL"
      cancelButtonText="Cancel"
      okButtonText="OK"
      okButtonWarningColor
      //Process Polling Options
      processPollingOptions={processPollingOptions}
      buildResultSection={buildResultSection}
      resetOnOpen={true}
    >
      Update Unit Schema
    </ActionButtonConfirmStatusWithPolling>
  );
}
